const ACCESS_TOKEN_KEY = "nova:accessToken";
const REFRESH_TOKEN_KEY = "nova:refreshToken";

export interface StoredTokens {
	accessToken: string;
	refreshToken: string; // Always required - ensures single token per user
}

const isBrowser =
	typeof window !== "undefined" && typeof localStorage !== "undefined";

export function getAccessToken(): string | null {
	if (!isBrowser) return null;
	return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
	if (!isBrowser) return null;
	return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Store tokens in localStorage
 * Always stores refreshToken to ensure single token per user is maintained
 */
export function setTokens(tokens: StoredTokens): void {
	if (!isBrowser) return;

	// Always store both tokens - refreshToken is required for single-token-per-user system
	localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
	localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);

	console.log("[TokenStorage] Tokens stored in localStorage");
}

/**
 * Check if user has valid tokens stored
 */
export function hasTokens(): boolean {
	if (!isBrowser) return false;
	return !!(getAccessToken() && getRefreshToken());
}

export function clearTokens(): void {
	if (!isBrowser) return;
	localStorage.removeItem(ACCESS_TOKEN_KEY);
	localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Decode JWT token to check expiration
 * Returns null if token is invalid or expired
 */
export function isTokenExpired(token: string | null): boolean {
	if (!token) return true;

	try {
		// JWT format: header.payload.signature
		const parts = token.split(".");
		if (parts.length !== 3) return true;

		// Decode payload (base64url)
		const payload = JSON.parse(
			atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
		);

		// Check if token has expiration
		if (!payload.exp) return true;

		// Check if token is expired (with 30 second buffer)
		const now = Math.floor(Date.now() / 1000);
		return payload.exp < now - 30; // 30 second buffer
	} catch (error) {
		console.error("[TokenStorage] Error decoding token:", error);
		return true;
	}
}

/**
 * Check if access token is expired or will expire soon
 */
export function isAccessTokenExpired(): boolean {
	const token = getAccessToken();
	return isTokenExpired(token);
}
